import { ml } from "../../core/common/matrixlib";
import { ILineEditorLine, LineEditorExt } from "../../core/common/UI/ILineEditor";
import { LineType } from "../../core/common/UI/LineType";
import { matrixSession, restConnection } from "../../core/globals";
import { XRGetUser_AllUsers_GetUserListAck } from "../../core/RestResult";
import {
    IDisplayedWidget,
    IWidgetParameters,
    IWidgetPlugin,
    widgetPluginManager,
    widgetRenderEvent,
} from "../../core/WidgetDashboard/WidgetPluginManager";
import { WidgetPluginsConstants } from "../../core/WidgetDashboard/WidgetPluginsContainer";
import { WidgetSettingsManager } from "../../core/WidgetDashboard/WidgetSettingsManager";

export { BaseWidget };

abstract class BaseWidget implements IWidgetPlugin {
    abstract _root: JQuery;
    abstract id: string;
    abstract defaultParameters(): IWidgetParameters;
    abstract displayedWidget: IDisplayedWidget;
    abstract getBoxConfigurator(): ILineEditorLine[];
    abstract help: string;
    abstract render(root: JQuery, arg0: IDisplayedWidget): void;
    abstract renderOn: widgetRenderEvent;

    pluginName() {
        return this.constructor.name;
    }

    mergeOptions(parameters: IWidgetParameters): IWidgetParameters {
        let newParameters: IWidgetParameters = ml.JSON.clone(this.defaultParameters());

        if (!newParameters.position) {
            newParameters.position = { h: 2, w: 3, dashboard: WidgetPluginsConstants.defaultDashboardId };
        }
        // make sure there is a parameter object
        if (!newParameters.options) {
            newParameters.options = { title: "", canBeHidden: true };
        }
        // copy parameters one by one
        if (parameters.options) {
            for (let name in parameters.options) {
                newParameters.options[name] = parameters.options[name];
            }
        }
        newParameters.users = parameters.users;
        if (parameters.position) {
            newParameters.position.x = parameters.position.x;
            newParameters.position.y = parameters.position.y;
            newParameters.position.w = parameters.position.w;
            newParameters.position.h = parameters.position.h;
            newParameters.position.h = parameters.position.h;
            newParameters.position.dashboard =
                parameters.position.dashboard ?? WidgetPluginsConstants.defaultDashboardId;
        }

        return newParameters;
    }

    addContainer(root: JQuery, displayedWidget: IDisplayedWidget) {
        let that = this;

        let id = displayedWidget.id;
        let configurator = this.getBoxConfigurator();
        let mWidget = $(
            "<div class='mWidget " +
                that.pluginName() +
                "' style='height:100%' id='" +
                id +
                "'><div class='toolbar' style='float:right;'> </div> </div>",
        );

        root.append(mWidget);

        $(".toolbar", mWidget).append(
            "<div class='widget-title'>" + displayedWidget.parameters.options.title ?? "" + " <div>",
        );

        if (WidgetSettingsManager.canBeEdited(displayedWidget, configurator)) {
            this.addToToolbar(
                "fa-cog",
                () => {
                    that.showWidgetSettingEditor(displayedWidget);
                },
                "settings",
            );
        }
        if (WidgetSettingsManager.canBeDeleted(displayedWidget)) {
            this.addToToolbar(
                "fa-trash-alt",
                () => {
                    WidgetSettingsManager.deleteDeleteWidgetSettings(displayedWidget)
                        .done(() => {
                            widgetPluginManager.removeLoadedWidget(displayedWidget);
                            ml.UI.widgetPluginsContainer.deleteWidget(displayedWidget);
                        })
                        .fail((error) => {
                            ml.UI.showError("Error when deleting the widget", "");
                        });
                },
                "remove",
            );
        }
        // cast to any to avoid compilation complain
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((<any>that).refresh) {
            this.addToToolbar(
                "fa-sync-alt",
                () => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (<any>that).refresh();
                },
                "refresh",
            );
        }

        that.updateHideUnHideButton();

        let container = $("<div class='mWidgetContainer'></div>");

        mWidget.append(container);
        return container;
    }
    updateHideUnHideButton() {
        let that = this;
        $(".toolbar i.fa-eye", this._root).remove();
        $(".toolbar i.fa-eye-slash", this._root).remove();
        if (that.displayedWidget.parameters.options.canBeHidden) {
            if (WidgetSettingsManager.getHiddenWidgets().indexOf(that.displayedWidget.id) >= 0) {
                this.addToToolbar(
                    "fa-eye",
                    () => {
                        that.unhide(true);
                    },
                    "unhide",
                );
            } else {
                this.addToToolbar(
                    "fa-eye-slash",
                    () => {
                        that.hide(true);
                    },
                    "hide",
                );
            }
        }
    }

    protected addToToolbar(iconName: string, onClick: () => void, tooltip = "") {
        let i = $("<i title='" + tooltip + "' class='settings fal " + iconName + "'></i>");
        i.insertBefore($(".toolbar .widget-title", this._root));
        i.click(() => {
            onClick();
        });
    }
    async showWidgetSettingEditor(displayedWidget: IDisplayedWidget) {
        let configurator = this.getBoxConfigurator();

        for (let key in displayedWidget.parameters.options) {
            let i = configurator.findIndex((o) => {
                return o.id == key;
            });
            if (i >= 0) configurator[i].value = displayedWidget.parameters.options[key];
        }
        let editUser = false;
        if (displayedWidget.parameters.users != undefined && displayedWidget.parameters.users.length > 0) {
            if (matrixSession.isAdmin()) {
                editUser = true;
                let userList = [{ id: "*", label: "Everyone" }];
                let allUsers = <XRGetUser_AllUsers_GetUserListAck>await restConnection.getServer("user?details=1");

                userList = userList.concat(
                    allUsers.user.map((u) => {
                        return {
                            id: u.login,
                            label:
                                (u.firstName ?? "") +
                                (u.firstName ? " " : "") +
                                (u.lastName ?? "") +
                                " [" +
                                u.login +
                                "]",
                        };
                    }),
                );
                configurator.push({
                    help: "Users",
                    type: LineType.select,
                    multiple: true,
                    required: true,
                    value: displayedWidget.parameters.users.join(","),
                    options: userList,
                });
            }
        }

        let editor = new LineEditorExt();
        editor.showDialog(
            "Settings [" + displayedWidget.id + "]",
            this.calculateHeight(configurator),
            configurator,
            (updated: ILineEditorLine[]) => {
                for (let i = 0; i < updated.length; i++) {
                    let paramId = updated[i].id;
                    if (paramId == undefined) {
                        if (editUser && i == updated.length - 1 && updated[i].value != "") {
                            displayedWidget.parameters.users = updated[i].value.split(",");
                        } else {
                            console.log("Cannot save parameter for widget " + displayedWidget.id);
                        }
                    } else {
                        displayedWidget.parameters.options[paramId] = updated[i].value;
                    }
                }

                WidgetSettingsManager.setSettings(displayedWidget)
                    .done(() => {
                        this.render(this._root, displayedWidget);
                    })
                    .fail((error) => {
                        ml.UI.showError("Error when saving the widget", "");
                    });
                return true;
            },
        );
    }

    calculateHeight(configurator: ILineEditorLine[]): number {
        let height = 150;
        configurator.forEach((c) => {
            switch (c.type) {
                case "richtext":
                    height += 225;
                    break;
                case "multiselect":
                    height += 225;
                    break;
                case "json":
                    height += 225;
                    break;
                case "select":
                    height += 125;
                    break;
                default:
                    height += 59;
            }
        });
        return Math.min(height, 900);
    }

    updatePosition(w: number, h: number, x: number, y: number) {
        let p = this.displayedWidget.parameters;
        if (p.position.x != x || p.position.y != y || p.position.w != w || p.position.h != h) {
            p.position.x = x;
            p.position.y = y;
            p.position.w = w;
            p.position.h = h;
            this.displayedWidget.parameters = p;
            WidgetSettingsManager.updatePosition(this.displayedWidget);
        }
    }

    hide(showConfirm = false) {
        WidgetSettingsManager.hide(this.displayedWidget).done(() => {
            if (showConfirm) {
                ml.UI.showSuccess("Widget hidden");
            }
        });
        this.updateHideUnHideButton();
        ml.UI.widgetPluginsContainer.hideWidget(this.displayedWidget.id);
    }

    unhide(showConfirm: boolean) {
        WidgetSettingsManager.unhide(this.displayedWidget).done(() => {
            if (showConfirm) {
                ml.UI.showSuccess("Widget unhidden");
            }
        });
        this.updateHideUnHideButton();
        ml.UI.widgetPluginsContainer.unhide(this.displayedWidget.id);
    }
}
